import { api } from './api'

export interface BillingStatus {
  billingStatus: 'pending_setup' | 'active' | 'past_due' | 'cancelled' | 'trialing'
  hasPaymentMethod: boolean
  hasSubscription: boolean
  paymentMethod: { last4: string; brand: string } | null
  setupSkipped: boolean
  setupSkippedAt: string | null
  setupCompletedAt: string | null
  cancelAt: string | null
  unbilledCount: number
  unbilledTotal: number
}

export interface UsageEvent {
  id: string
  clinic_id: string
  event_type: 'mdi_visit' | 'consulting_visit' | 'lab_order' | 'at_home_phlebotomy'
  encounter_id: string | null
  patient_id: string | null
  provider_id: string | null
  provider?: { id: string; first_name: string; last_name: string }
  amount: number
  stripe_usage_record_id: string | null
  billed_at: string | null
  created_at: string
}

export interface UsageSummary {
  mdi_visit: number
  consulting_visit: number
  lab_order: number
  at_home_phlebotomy: number
  totalAmount: number
}

export interface Invoice {
  id: string
  number: string
  status: string
  amountDue: number
  amountPaid: number
  currency: string
  periodStart: string | null
  periodEnd: string | null
  created: string
  hostedUrl: string
  pdfUrl: string
}

export interface PricingService {
  name: string
  type: string
  price: number
  unit: string
  description: string
}

export interface AdminClinicBilling {
  clinicId: string
  clinicName: string
  billingStatus: string
  hasPaymentMethod: boolean
  paymentMethod: { last4: string; brand: string } | null
  setupCompletedAt: string | null
  unbilled: { count: number; total: number }
}

export interface RevenueData {
  billed: Record<string, { count: number; total: number }>
  unbilled: Record<string, { count: number; total: number }>
  totals: { billed: number; unbilled: number; combined: number }
}

export interface OrgBillingOverview {
  totalBilled: number
  totalUnbilled: number
  activeSubscriptions: number
  clinicsNeedingSetup: number
  clinics: Array<{
    id: string
    name: string
    billingStatus: 'active' | 'pending_setup' | 'past_due' | 'cancelled' | 'trialing' | 'none'
    unbilledTotal: number
    eventCount: number
    setupDate: string | null
  }>
}

export interface UsageTrendPoint {
  period: string
  mdi_visit: number
  consulting_visit: number
  lab_order: number
  at_home_phlebotomy: number
}

export const billingService = {
  async createSetupIntent(clinicId: string): Promise<{ clientSecret: string; customerId: string } | null> {
    try {
      const response = await api.post(`/billing/setup-intent/${clinicId}`)
      return response.data
    } catch (error) {
      console.error('Failed to create setup intent:', error)
      return null
    }
  },

  async subscribe(
    clinicId: string,
    paymentMethodId: string
  ): Promise<{ success: boolean; data?: { subscriptionId: string; status: string }; error?: string }> {
    try {
      const response = await api.post(`/billing/clinics/${clinicId}/subscribe`, { paymentMethodId })
      return { success: true, data: response.data }
    } catch (error: any) {
      return { success: false, error: error.message || 'Subscription failed' }
    }
  },

  async skipBillingSetup(clinicId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await api.post(`/billing/clinics/${clinicId}/skip-setup`)
      return { success: true }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to skip billing setup' }
    }
  },

  async getUsage(
    clinicId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{ events: UsageEvent[]; summary: UsageSummary } | null> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const response = await api.get(`/billing/clinics/${clinicId}/usage?${params.toString()}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch usage:', error)
      return null
    }
  },

  async getInvoices(clinicId: string): Promise<Invoice[]> {
    try {
      const response = await api.get(`/billing/clinics/${clinicId}/invoices`)
      return response.data?.invoices || []
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
      return []
    }
  },

  async getInvoicePdf(clinicId: string, invoiceId: string): Promise<{ hostedUrl: string; pdfUrl: string } | null> {
    try {
      const response = await api.get(`/billing/clinics/${clinicId}/invoice/${invoiceId}/pdf`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch invoice PDF:', error)
      return null
    }
  },

  async getBillingStatus(clinicId: string): Promise<BillingStatus | null> {
    try {
      const response = await api.get<{ success?: boolean; data?: BillingStatus }>(
        `/billing/clinics/${clinicId}/status`
      )
      return response?.data ?? (response as unknown as BillingStatus) ?? null
    } catch (error) {
      console.error('Failed to fetch billing status:', error)
      return null
    }
  },

  async getPricing(): Promise<{ services: PricingService[]; billingCycle: string; currency: string } | null> {
    try {
      const response = await api.get('/billing/pricing')
      return response.data
    } catch (error) {
      console.error('Failed to fetch pricing:', error)
      return null
    }
  },

  async updatePaymentMethod(
    clinicId: string,
    paymentMethodId: string
  ): Promise<{ success: boolean; data?: { last4: string; brand: string }; error?: string }> {
    try {
      const response = await api.patch(`/billing/clinics/${clinicId}/payment-method`, { paymentMethodId })
      return { success: true, data: response.data }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update payment method' }
    }
  },

  // Admin endpoints
  async getAdminOverview(): Promise<{ clinics: AdminClinicBilling[] } | null> {
    try {
      const response = await api.get('/admin/billing/overview')
      return response.data
    } catch (error) {
      console.error('Failed to fetch admin overview:', error)
      return null
    }
  },

  async getAdminRevenue(startDate?: string, endDate?: string): Promise<RevenueData | null> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const response = await api.get(`/admin/billing/revenue?${params.toString()}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch admin revenue:', error)
      return null
    }
  },

  async getAdminUsageTrends(params?: { start_date?: string; end_date?: string; granularity?: string }): Promise<{ trends: UsageTrendPoint[]; granularity: string } | null> {
    try {
      const qs = new URLSearchParams()
      if (params?.start_date) qs.set('start_date', params.start_date)
      if (params?.end_date) qs.set('end_date', params.end_date)
      if (params?.granularity) qs.set('granularity', params.granularity)
      const response = await api.get(`/admin/billing/usage-trends?${qs.toString()}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch admin usage trends:', error)
      return null
    }
  },

  async getOrgBillingOverview(orgId: string): Promise<OrgBillingOverview | null> {
    try {
      const response = await api.get(`/organizations/${orgId}/billing/overview`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch org billing overview:', error)
      return null
    }
  },

  async getOrgBillingRevenue(orgId: string, params?: { start_date?: string; end_date?: string }): Promise<RevenueData | null> {
    try {
      const qs = new URLSearchParams()
      if (params?.start_date) qs.set('start_date', params.start_date)
      if (params?.end_date) qs.set('end_date', params.end_date)
      const response = await api.get(`/organizations/${orgId}/billing/revenue?${qs.toString()}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch org billing revenue:', error)
      return null
    }
  },

  async getOrgInvoices(orgId: string): Promise<unknown[]> {
    try {
      const response = await api.get(`/organizations/${orgId}/billing/invoices`)
      return response.data?.data?.invoices || []
    } catch {
      return []
    }
  },

  async exportOrgBillingCSV(orgId: string): Promise<Blob> {
    const response = await api.get(`/organizations/${orgId}/billing/export-csv`, {
      responseType: 'blob',
    })
    return response.data
  },

  async getOrgUsageTrends(orgId: string, params?: { start_date?: string; end_date?: string; granularity?: string }): Promise<{ trends: UsageTrendPoint[]; granularity: string } | null> {
    try {
      const qs = new URLSearchParams()
      if (params?.start_date) qs.set('start_date', params.start_date)
      if (params?.end_date) qs.set('end_date', params.end_date)
      if (params?.granularity) qs.set('granularity', params.granularity)
      const response = await api.get(`/organizations/${orgId}/billing/usage-trends?${qs.toString()}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch org usage trends:', error)
      return null
    }
  },
}
